import React, { useRef } from 'react'
import styled, { css, keyframes } from 'styled-components'
import Box from '@ui/Box'
import { BoldBlue } from '@components/Landing/Common'
import { useIsVisible } from '@utils/hooks'
import { isMobile } from '@utils/index'

const keyframes1 = keyframes`
  0% {
    transform: rotate(-15deg) scale(1.1)
  }

  20% {
    transform: rotate(35deg) scale(0.9)
  }

  40% {
    transform: rotate(-25deg) scale(1.1)
  }

  60% {
    transform: rotate(45deg) scale(0.8)
  }

  80% {
    transform: rotate(-15deg) scale(1.1)
  }

  100% {
    transform: rotate(0) scale(1);
  }
`

const animation1 = css`
  animation: ${keyframes1} 1000ms ease 1;
`

const keyframes1_1 = keyframes`
  0% {
    transform: translate(0, 0)
  }

  100% {
    transform: translate(-10px, 10px)
  }
`

const animation1_1 = css`
  animation: ${keyframes1_1} 1000ms ease 1 forwards;
`

const keyframes1_2 = keyframes`
  0% {
    transform: translate(-20px, 20px) scale(0.8)
  }

  100% {
    transform: translate(0, 0) scale(1)
  }
`

const animation1_2 = css`
  animation: ${keyframes1_2} 1000ms ease 1;
`

const keyframes2_1 = keyframes`
  0% {
    transform: translateX(20px)
  }

  100% {
    transform: translateY(0)
  }
`

const animation2_1 = css`
  animation: ${keyframes2_1} 1000ms 300ms ease 1 forwards;
`

const keyframes2_2 = keyframes`
  0% {
    transform: translateX(-20px)
  }

  100% {
    transform: translateY(0)
  }
`

const animation2_2 = css`
  animation: ${keyframes2_2} 1000ms 300ms ease 1 forwards;
`

const keyframes3_1 = keyframes`
  0% {
    transform: rotate(-35deg) scale(1.2)
  }

  50% {
    transform: rotate(15deg) scale(0.9)
  }

  100% {
    transform: rotate(0deg) scale(1)
  }
`

const animation3_1 = css`
  animation: ${keyframes3_1} 1000ms ease 1;
`

const keyframes3_2 = keyframes`
  0% {
    transform: rotate(15deg) scale(1.1)
  }

  50% {
    transform: rotate(-15deg) scale(0.9)
  }

  100% {
    transform: rotate(0deg) scale(1)
  }
`

const animation3_2 = css`
  animation: ${keyframes3_2} 1000ms 200ms ease 1;
`

const Hello = styled(Box)`
  margin-top: 390px;

  @media (max-width: 768px) {
    margin-top: 160px;
  }
`

const HelloTitle = styled.h2`
  font-size: 68px;
  font-weight: 500;
  max-width: 975px;

  @media (max-width: 768px) {
    font-size: 56px;
  }
`

const HelloText = styled.div`
  font-size: 48px;

  @media (max-width: 768px) {
    font-size: 38px;
  }
`

const HelloIcon = styled.svg<{ $visible: boolean }>`
  ${({ $visible }) => $visible && animation1}
`

const HelloItem = styled.div`
  position: relative;
  background: linear-gradient(to right, #5c64ff, #41e0ec);
  padding: 1px;
  border-radius: 20px;
  transition: padding ease 300ms;

  &:last-child {
    margin-top: 60px;
  }

  @media (max-width: 768px) {
    margin-top: 30px;

    &:last-child {
      margin-top: 30px;
    }
  }
`

const HelloItemCover = styled.div`
  background: #171717;
  border-radius: 20px;
  padding: 50px;
`

const HelloItemIcon = styled.div`
  position: relative;
  overflow: hidden;
`

const HelloItemIcon1p1 = styled.svg<{ $visible: boolean }>`
  position: absolute;
  top: 40px;
  left: 0;
  ${({ $visible }) => $visible && animation1_1}
`

const HelloItemIcon1p2 = styled.svg<{ $visible: boolean }>`
  position: relative;
  top: 40px;
  margin-bottom: 40px;
  ${({ $visible }) => $visible && animation1_2}
`

const HelloItemIcon2p1 = styled.svg<{ $visible: boolean }>`
  position: absolute;
  top: 11px;
  left: 0;
  transform: translateX(20px);
  ${({ $visible }) => $visible && animation2_1}
`
const HelloItemIcon2p2 = styled.svg<{ $visible: boolean }>`
  background-color: #171717;
  z-index: 1;
  position: relative;
  left: 20px;
`
const HelloItemIcon2p3 = styled.svg<{ $visible: boolean }>`
  position: absolute;
  top: 11px;
  left: 74px;
  transform: translateX(-20px);
  ${({ $visible }) => $visible && animation2_2}
`

const HelloItemIcon3p1 = styled.svg<{ $visible: boolean }>`
  position: relative;
  top: -40px;
  ${({ $visible }) => $visible && animation3_1}
`
const HelloItemIcon3p2 = styled.svg<{ $visible: boolean }>`
  position: relative;
  top: 40px;
  margin-bottom: 40px;
  ${({ $visible }) => $visible && animation3_2}
`

const HelloItemTitle = styled.h3`
  font-size: 32px;
  font-weight: 500;
  margin-bottom: 16px;
  margin-top: 46px;
`

const HelloItemText = styled.div`
  font-size: 20px;
`

const HelloContent = styled.div`
  display: flex;
  align-items: center;
  margin-top: 90px;

  @media (max-width: 768px) {
    display: block;
  }
`

const HelloContentLeft = styled.div`
  max-width: 50%;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`

const HelloContentRight = styled.div`
  margin-left: 40px;
  max-width: 50%;

  @media (max-width: 768px) {
    margin-left: 0;
    max-width: 100%;
  }
`

const Anchor = styled.div`
  position: relative;
  top: 50px;
`

const HelloComponent = () => {
  const anchor1 = useRef<HTMLDivElement | null>(null)
  const anchor2 = useRef<HTMLDivElement | null>(null)

  const anchor1visible = useIsVisible(anchor1)
  const anchor2visible = useIsVisible(anchor2)

  return (
    <Hello>
      <HelloTitle>
        Say hello{' '}
        <HelloIcon
          xmlns="http://www.w3.org/2000/svg"
          width="77"
          height="77"
          viewBox="0 0 77 77"
          fill="none"
          $visible={anchor1visible}
        >
          <path
            opacity="0.5"
            d="M28.1809 70.583C22.7742 68.17 18.3084 64.4099 15.373 59.376"
            stroke="#40E0EC"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            opacity="0.5"
            d="M47.5645 7.01358C52.9445 5.48279 58.4988 6.94011 62.3757 10.8998"
            stroke="#40E0EC"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M33.4193 30.5595C34.0889 31.7075 35.5621 32.0954 36.7101 31.426C37.858 30.7566 38.2459 29.2832 37.5766 28.1352L33.4193 30.5595ZM24.1843 38.9253C24.8537 40.0733 26.3271 40.4612 27.4751 39.7916C28.6231 39.1223 29.011 37.6491 28.3416 36.5008L24.1843 38.9253ZM51.4854 32.5613L52.6795 34.6505C53.2368 34.332 53.6436 33.8042 53.8098 33.1843C53.976 32.5642 53.8875 31.9037 53.5641 31.3492L51.4854 32.5613ZM44.2233 49.0871C44.8929 50.235 46.3662 50.6229 47.5141 49.9537C48.6624 49.2841 49.0503 47.8108 48.3807 46.6629L44.2233 49.0871ZM14.061 28.9314C16.094 27.7695 18.0954 28.4837 18.7822 29.6614L22.9395 27.2371C20.6427 23.2986 15.4862 22.5738 11.6731 24.7531L14.061 28.9314ZM11.6731 24.7531C7.85241 26.9367 5.88798 31.7521 8.19666 35.7112L12.3539 33.287C11.6791 32.1298 12.0354 30.089 14.061 28.9314L11.6731 24.7531ZM22.6151 12.0319L25.3161 16.6638L29.4734 14.2395L26.7724 9.60765L22.6151 12.0319ZM25.3161 16.6638L33.4193 30.5595L37.5766 28.1352L29.4734 14.2395L25.3161 16.6638ZM28.3416 36.5008L22.9395 27.2371L18.7822 29.6614L24.1843 38.9253L28.3416 36.5008ZM22.9395 27.2371L16.1868 15.6574L12.0296 18.0817L18.7822 29.6614L22.9395 27.2371ZM17.8939 11.3019C19.9269 10.14 21.9283 10.8542 22.6151 12.0319L26.7724 9.60765C24.4756 5.6691 19.3191 4.94437 15.506 7.12363L17.8939 11.3019ZM15.506 7.12363C11.6853 9.30719 9.72088 14.1226 12.0296 18.0817L16.1868 15.6574C15.512 14.5002 15.8683 12.4596 17.8939 11.3019L15.506 7.12363ZM35.9016 10.6141L44.0049 24.5097L48.1622 22.0855L40.0589 8.18979L35.9016 10.6141ZM44.0049 24.5097L49.407 33.7734L53.5641 31.3492L48.1622 22.0855L44.0049 24.5097ZM31.1805 9.88405C33.2136 8.72215 35.215 9.43639 35.9016 10.6141L40.0589 8.18979C37.7621 4.25124 32.6056 3.52651 28.7925 5.70577L31.1805 9.88405ZM28.7925 5.70577C24.9719 7.88933 23.0075 12.7048 25.3161 16.6638L29.4734 14.2395C28.7986 13.0823 29.1548 11.0417 31.1805 9.88405L28.7925 5.70577ZM54.5904 18.46L62.6934 32.3556L66.8508 29.9314L58.7475 16.0357L54.5904 18.46ZM49.869 17.73C51.9022 16.5681 53.9035 17.2823 54.5904 18.46L58.7475 16.0357C56.4509 12.0972 51.2945 11.3724 47.4811 13.5517L49.869 17.73ZM47.4811 13.5517C43.6606 15.7352 41.6961 20.5507 44.0049 24.5097L48.1622 22.0855C47.4872 20.9283 47.8436 18.8876 49.869 17.73L47.4811 13.5517ZM8.19666 35.7112L20.3514 56.5548L24.5087 54.1303L12.3539 33.287L8.19666 35.7112ZM53.2112 59.6813C42.409 65.8551 29.6707 62.9824 24.5087 54.1303L20.3514 56.5548C27.1234 68.1677 43.0167 71.0507 55.5991 63.8599L53.2112 59.6813ZM62.6934 32.3556C67.8434 41.1872 64.0062 53.512 53.2112 59.6813L55.5991 63.8599C68.1893 56.6645 73.6348 41.5648 66.8508 29.9314L62.6934 32.3556ZM48.3807 46.6629C46.2141 42.9473 47.7307 37.4787 52.6795 34.6505L50.2916 30.4722C43.548 34.3265 40.4231 42.57 44.2233 49.0871L48.3807 46.6629Z"
            fill="#40E0EC"
          />
        </HelloIcon>
        <HelloText>
          to our user-friendly <BoldBlue>pricing table builder</BoldBlue> that&nbsp;will
          make your pricing page look fantastic!
        </HelloText>
      </HelloTitle>

      <HelloContent>
        <HelloContentLeft>
          <HelloItem>
            <HelloItemCover>
              <HelloItemIcon>
                <HelloItemIcon1p2
                  width="130"
                  height="131"
                  viewBox="0 0 130 131"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  $visible={anchor2visible}
                >
                  <path
                    d="M103.509 51.1162L77.6813 76.8345L83.0551 82.2312L108.882 56.5129L103.509 51.1162ZM58.7895 76.8345L52.7232 70.7935L47.3492 76.1902L53.4157 82.2312L58.7895 76.8345ZM52.7232 52.0267L78.55 26.3085L73.1761 20.9118L47.3493 46.63L52.7232 52.0267ZM94.0623 19.9115H96.5756V12.2955H94.0623V19.9115ZM109.926 33.1897V35.6919L117.542 35.6919V33.1897H109.926ZM96.5756 19.9115C100.727 19.9115 103.459 19.9195 105.484 20.1906C107.4 20.4472 108.104 20.8755 108.534 21.304L113.908 15.9073C111.825 13.8337 109.25 13.0109 106.495 12.642C103.847 12.2875 100.513 12.2955 96.5756 12.2955V19.9115ZM117.542 33.1897C117.542 29.2705 117.55 25.9465 117.194 23.3061C116.822 20.5545 115.993 17.9833 113.908 15.9073L108.534 21.304C108.962 21.7303 109.389 22.4239 109.646 24.3251C109.918 26.3375 109.926 29.0543 109.926 33.1897H117.542ZM52.7232 70.7935C49.787 67.87 47.8624 65.9416 46.6245 64.326C45.4535 62.7977 45.265 62.0072 45.265 61.4101H37.649C37.649 64.3519 38.8903 66.7545 40.579 68.958C42.2009 71.0753 44.5659 73.4185 47.3492 76.1902L52.7232 70.7935ZM53.4157 82.2312C56.1991 85.0029 58.5524 87.3578 60.6778 88.9724C62.8915 90.654 65.2961 91.8837 68.2354 91.8837V84.2677C67.621 84.2677 66.8193 84.0738 65.2849 82.908C63.6617 81.6747 61.7257 79.758 58.7895 76.8345L53.4157 82.2312ZM108.882 56.5129C112.381 53.0292 114.931 50.5849 116.27 47.3664L109.238 44.4414C108.618 45.9327 107.449 47.1922 103.509 51.1162L108.882 56.5129ZM109.926 35.6919C109.926 41.2416 109.858 42.9517 109.238 44.4414L116.27 47.3664C117.609 44.1461 117.542 40.6182 117.542 35.6919L109.926 35.6919ZM78.55 26.3085C82.489 22.3863 83.7558 21.2194 85.2592 20.5993L82.3555 13.5586C79.1288 14.8894 76.6765 17.4263 73.1761 20.9118L78.55 26.3085ZM94.0623 12.2955C89.1119 12.2955 85.5801 12.2286 82.3555 13.5586L85.2592 20.5993C86.7646 19.9784 88.4924 19.9115 94.0623 19.9115V12.2955ZM77.6813 76.8345C75.4183 79.0883 73.7331 80.7634 72.2891 82.0083C70.839 83.2594 69.9048 83.8306 69.2128 84.0849L71.8423 91.2328C73.8154 90.5068 75.5737 89.2334 77.2639 87.7757C78.9602 86.3124 80.8627 84.4145 83.0551 82.2312L77.6813 76.8345ZM69.2128 84.0849C68.8563 84.2159 68.5492 84.2677 68.2354 84.2677V91.8837C69.492 91.8837 70.6877 91.6573 71.8423 91.2328L69.2128 84.0849ZM47.3493 46.63C45.2091 48.7612 43.3438 50.6158 41.8928 52.2696C40.4488 53.9157 39.1807 55.618 38.4191 57.5097L45.484 60.354C45.766 59.6533 46.3671 58.7181 47.6179 57.2924C48.8618 55.8746 50.5172 54.2234 52.7232 52.0267L47.3493 46.63ZM38.4191 57.5097C37.9202 58.7485 37.649 60.0397 37.649 61.4101H45.265C45.265 61.0811 45.3235 60.7526 45.484 60.354L38.4191 57.5097Z"
                    fill="#40E0EC"
                  />
                  <path
                    opacity="0.5"
                    d="M41.1741 58.1562L43.8651 55.4618L43.861 55.4578L41.1741 58.1562ZM60.1066 40.4473C61.8756 41.5845 64.2315 41.0723 65.3688 39.3032C66.5061 37.5341 65.9938 35.178 64.2249 34.0408L60.1066 40.4473ZM71.7708 88.9015L69.0798 91.5956C69.1387 91.6545 69.1996 91.7113 69.2621 91.7662L71.7708 88.9015ZM95.7023 65.3859C94.5579 63.6215 92.2 63.1183 90.4356 64.2622C88.6707 65.4062 88.1675 67.7641 89.3115 69.529L95.7023 65.3859ZM74.0805 90.9238L76.7674 88.2257L76.6811 88.1394L76.5892 88.0592L74.0805 90.9238ZM73.2199 84.9666C71.7317 83.4805 69.3205 83.4815 67.8343 84.9697C66.3482 86.4578 66.3497 88.869 67.8379 90.3552L73.2199 84.9666ZM39.2615 61.6281C40.7495 63.1143 43.1607 63.1127 44.6469 61.6246C46.1331 60.1369 46.1316 57.7257 44.6436 56.2393L39.2615 61.6281ZM64.2249 34.0408L61.4455 32.2541L57.3268 38.6606L60.1066 40.4473L64.2249 34.0408ZM61.4455 32.2541C58.7068 30.4936 56.4728 29.0543 54.5449 28.0169C52.5694 26.9537 50.6875 26.1824 48.5915 25.8731L47.4794 33.4074C48.3709 33.539 49.3765 33.8842 50.9356 34.7233C52.5426 35.5882 54.4901 36.837 57.3268 38.6606L61.4455 32.2541ZM29.0713 45.3777C31.5604 42.899 34.22 40.2518 36.6076 38.0762C37.8005 36.9894 38.8801 36.0622 39.8005 35.3536C40.7798 34.5997 41.3637 34.2632 41.5987 34.1658L38.683 27.13C37.4842 27.6268 36.2503 28.4753 35.1547 29.3188C34.0001 30.2075 32.7476 31.2901 31.4783 32.4466C28.9417 34.7578 26.1567 37.5321 23.6974 39.9809L29.0713 45.3777ZM48.5915 25.8731C45.2559 25.3807 41.8294 25.8261 38.683 27.13L41.5987 34.1658C43.5095 33.374 45.5461 33.122 47.4794 33.4074L48.5915 25.8731ZM26.1286 51.7471L27.8012 52.409L30.6038 45.3274L28.9313 44.6655L26.1286 51.7471ZM34.2433 56.6285L38.4871 60.8543L43.861 55.4578L39.6172 51.2319L34.2433 56.6285ZM27.8012 52.409C28.0835 52.5207 28.1924 52.5639 28.2977 52.6072L31.1959 45.5642C31.0321 45.4968 30.8678 45.4319 30.6038 45.3274L27.8012 52.409ZM39.6172 51.2319C39.4163 51.0317 39.2914 50.9072 39.1642 50.784L33.8641 56.2532C33.9457 56.3323 34.0286 56.4147 34.2433 56.6285L39.6172 51.2319ZM28.2977 52.6072C30.3698 53.4599 32.2588 54.6976 33.8641 56.2532L39.1642 50.784C36.8644 48.5553 34.1599 46.7839 31.1959 45.5642L28.2977 52.6072ZM23.6974 39.9809C20.0217 43.6411 21.322 49.8448 26.1286 51.7471L28.9313 44.6655C29.2061 44.7742 29.2987 45.1512 29.0713 45.3777L23.6974 39.9809ZM89.3115 69.529L91.1058 72.2966L97.4961 68.1535L95.7023 65.3859L89.3115 69.529ZM84.3616 100.39L83.9904 100.759L89.3643 106.156L89.7354 105.786L84.3616 100.39ZM91.1058 72.2966C92.9372 75.1222 94.1908 77.0602 95.059 78.6595C95.9008 80.2112 96.2451 81.2084 96.3761 82.0888L103.909 80.9672C103.598 78.8733 102.821 76.9947 101.752 75.0267C100.71 73.1059 99.2641 70.8806 97.4961 68.1535L91.1058 72.2966ZM89.7354 105.786C92.1949 103.337 94.9808 100.564 97.3017 98.0382C98.4634 96.7739 99.5504 95.5269 100.444 94.3769C101.29 93.2858 102.144 92.0551 102.645 90.8578L95.6175 87.9211C95.521 88.1511 95.1849 88.7309 94.4274 89.7063C93.716 90.6222 92.7854 91.6971 91.6937 92.8852C89.509 95.2629 86.8505 97.9108 84.3616 100.39L89.7354 105.786ZM96.3761 82.0888C96.6614 84.005 96.4101 86.0242 95.6175 87.9211L102.645 90.8578C103.956 87.7185 104.405 84.2979 103.909 80.9672L96.3761 82.0888ZM76.5892 88.0592L74.2795 86.0364L69.2621 91.7662L71.5718 93.789L76.5892 88.0592ZM85.242 101.055C84.2981 98.6896 83.7157 97.2131 82.9485 95.8164L76.2729 99.4822C76.7644 100.377 77.1589 101.348 78.1687 103.878L85.242 101.055ZM71.3935 93.6224C73.327 95.5478 74.0668 96.2936 74.6979 97.0974L80.6891 92.3963C79.7051 91.1422 78.5749 90.0257 76.7674 88.2257L71.3935 93.6224ZM82.9485 95.8164C82.29 94.6166 81.5345 93.4732 80.6891 92.3963L74.6979 97.0974C75.2868 97.8488 75.8139 98.646 76.2729 99.4822L82.9485 95.8164ZM83.9904 100.759C84.195 100.556 84.4692 100.51 84.6586 100.548C84.85 100.586 85.1181 100.744 85.242 101.055L78.1687 103.878C80.0006 108.468 85.9406 109.565 89.3643 106.156L83.9904 100.759ZM74.4618 86.207L73.2199 84.9666L67.8379 90.3552L69.0798 91.5956L74.4618 86.207ZM38.4831 60.8503L39.2615 61.6281L44.6436 56.2393L43.8651 55.4618L38.4831 60.8503Z"
                    fill="#40E0EC"
                  />
                  <path
                    d="M30.2029 71.9531L21.6134 80.5323C19.4571 82.6861 15.961 82.6861 13.8047 80.5323C11.6484 78.3785 11.6484 74.8868 13.8047 72.733L14.5856 71.9531M58.2679 100.232L49.6783 108.811C47.522 110.965 47.522 114.457 49.6783 116.61C51.8346 118.764 55.3306 118.764 57.487 116.61L58.2679 115.83M37.2494 93.0687L28.5693 101.558"
                    stroke="#40E0EC"
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                  <path
                    opacity="0.5"
                    d="M64.8559 70.1524C66.3461 68.6682 66.3512 66.2575 64.8671 64.7673C63.383 63.2766 60.9723 63.2715 59.4821 64.7556L64.8559 70.1524ZM55.7563 79.2139L64.8559 70.1524L59.4821 64.7556L50.3822 73.8172L55.7563 79.2139ZM89.8141 46.6331C87.9497 48.4898 84.9195 48.4898 83.0551 46.6331L77.6813 52.0298C82.5175 56.8457 90.3518 56.8457 95.1879 52.0298L89.8141 46.6331ZM83.0551 46.6331C81.1994 44.7852 81.1994 41.7959 83.0551 39.948L77.6813 34.5513C72.836 39.376 72.836 47.2051 77.6813 52.0298L83.0551 46.6331ZM83.0551 39.948C84.9195 38.0913 87.9497 38.0913 89.8141 39.948L95.1879 34.5513C90.3518 29.7355 82.5175 29.7355 77.6813 34.5513L83.0551 39.948ZM89.8141 39.948C91.6699 41.7959 91.6699 44.7852 89.8141 46.6331L95.1879 52.0298C100.033 47.2051 100.033 39.376 95.1879 34.5513L89.8141 39.948Z"
                    fill="#40E0EC"
                  />
                </HelloItemIcon1p2>

                <HelloItemIcon1p1
                  width="130"
                  height="131"
                  viewBox="0 0 130 131"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  $visible={anchor2visible}
                >
                  <path
                    opacity="0.5"
                    d="M56.019 93.2057C57.5062 91.7185 57.5062 89.3073 56.019 87.8202C54.5319 86.333 52.1208 86.333 50.6337 87.8202L56.019 93.2057ZM39.959 98.4948C38.4718 99.9819 38.4718 102.393 39.959 103.88C41.4461 105.367 43.8572 105.367 45.3443 103.88L39.959 98.4948ZM41.9258 79.1293C43.413 77.6421 43.413 75.2314 41.9258 73.7443C40.4387 72.2571 38.0276 72.2571 36.5405 73.7443L41.9258 79.1293ZM25.7923 84.4925C24.3052 85.9796 24.3052 88.3909 25.7923 89.8775C27.2794 91.3647 29.6905 91.3647 31.1776 89.8775L25.7923 84.4925ZM50.6337 87.8202L39.959 98.4948L45.3443 103.88L56.019 93.2057L50.6337 87.8202ZM36.5405 73.7443L25.7923 84.4925L31.1776 89.8775L41.9258 79.1293L36.5405 73.7443Z"
                    fill="#40E0EC"
                  />
                </HelloItemIcon1p1>
              </HelloItemIcon>
              <HelloItemTitle>Boost your revenue</HelloItemTitle>
              <HelloItemText>
                Uncover the ideal pricing table for your customers using A/B testing and
                analytics. Maximise your potential and watch as visitors seamlessly
                transition into satisfied customers.
              </HelloItemText>
            </HelloItemCover>
          </HelloItem>
          {isMobile && <Anchor ref={anchor2} />}
        </HelloContentLeft>

        <HelloContentRight>
          <HelloItem>
            <HelloItemCover>
              <HelloItemIcon>
                <HelloItemIcon2p1
                  width="20"
                  height="70"
                  viewBox="0 0 20 70"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  $visible={anchor2visible}
                >
                  <path
                    opacity="0.5"
                    d="M20.0007 66.25C14.1668 66.25 11.2498 66.25 9.02157 65.1146C7.06157 64.1158 5.46803 62.5225 4.46932 60.5625C3.33398 58.3342 3.33398 55.4171 3.33398 49.5833V20.4167C3.33398 14.5828 3.33398 11.6658 4.46932 9.43758C5.46803 7.47758 7.06157 5.88404 9.02157 4.88533C11.2498 3.75 14.1668 3.75 20.0007 3.75"
                    stroke="#40E0EC"
                    strokeWidth="6"
                  />
                </HelloItemIcon2p1>

                <HelloItemIcon2p2
                  width="56"
                  height="90"
                  viewBox="0 0 56 90"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  $visible={anchor2visible}
                >
                  <path
                    d="M3 28.333C3 16.5479 3 10.6553 6.66117 6.99417C10.3223 3.33301 16.2149 3.33301 28 3.33301C39.785 3.33301 45.6775 3.33301 49.3387 6.99417C53 10.6553 53 16.5479 53 28.333V61.6663C53 73.4513 53 79.3438 49.3387 83.0051C45.6775 86.6663 39.785 86.6663 28 86.6663C16.2149 86.6663 10.3223 86.6663 6.66117 83.0051C3 79.3438 3 73.4513 3 61.6663V28.333Z"
                    stroke="#40E0EC"
                    strokeWidth="6"
                  />
                </HelloItemIcon2p2>

                <HelloItemIcon2p3
                  width="20"
                  height="70"
                  viewBox="0 0 20 70"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  $visible={anchor2visible}
                >
                  <path
                    opacity="0.5"
                    d="M0 66.25C5.83375 66.25 8.75083 66.25 10.9792 65.1146C12.9392 64.1158 14.5325 62.5225 15.5313 60.5625C16.6667 58.3342 16.6667 55.4171 16.6667 49.5833V20.4167C16.6667 14.5828 16.6667 11.6658 15.5313 9.43758C14.5325 7.47758 12.9392 5.88404 10.9792 4.88533C8.75083 3.75 5.83375 3.75 0 3.75"
                    stroke="#40E0EC"
                    strokeWidth="6"
                  />
                </HelloItemIcon2p3>
              </HelloItemIcon>

              <HelloItemTitle>Quick & easy creation</HelloItemTitle>
              <HelloItemText>
                Create enchanting pricing templates in just minutes. No technical skills
                needed.
              </HelloItemText>
            </HelloItemCover>
          </HelloItem>

          <HelloItem>
            <HelloItemCover>
              {!isMobile && <Anchor ref={anchor2} />}
              <HelloItemIcon>
                <HelloItemIcon3p1
                  xmlns="http://www.w3.org/2000/svg"
                  width="132"
                  height="132"
                  viewBox="0 0 132 132"
                  fill="none"
                  $visible={anchor2visible}
                >
                  <g clipPath="url(#clip0_560_2896)">
                    <path
                      opacity="0.5"
                      d="M81.7903 103.908C102.98 94.9341 112.883 70.4812 103.908 49.2914C94.934 28.1016 70.4811 18.199 49.2913 27.1734C28.1015 36.1477 18.199 60.6006 27.1733 81.7904C36.1476 102.98 60.6005 112.883 81.7903 103.908Z"
                      stroke="#40E0EC"
                      strokeWidth="6"
                    />
                    <path
                      d="M60.5293 85.7627C64.8162 86.7992 69.385 86.5371 73.6645 84.7246C77.944 82.9121 81.3112 79.8132 83.5498 76.013"
                      stroke="#40E0EC"
                      strokeWidth="6"
                      strokeLinecap="round"
                    />
                    <path
                      d="M78.4508 53.2855C79.7969 56.4639 79.1705 59.7681 77.0514 60.6656C74.9324 61.563 72.1234 59.7138 70.7773 56.5354C69.4311 53.3569 70.0576 50.0528 72.1766 49.1553C74.2956 48.2579 77.1046 50.107 78.4508 53.2855Z"
                      fill="#40E0EC"
                    />
                    <path
                      d="M54.03 70.4156C56.1489 69.5181 56.7754 66.214 55.4293 63.0355C54.0831 59.857 51.2741 58.0079 49.1551 58.9053C47.0361 59.8028 46.4096 63.1069 47.7558 66.2854C49.1019 69.4639 51.911 71.313 54.03 70.4156Z"
                      fill="#40E0EC"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_560_2896">
                      <rect
                        width="100"
                        height="100"
                        fill="white"
                        transform="translate(0 38.999) rotate(-22.9537)"
                      />
                    </clipPath>
                  </defs>
                </HelloItemIcon3p1>
                <HelloItemIcon3p2
                  xmlns="http://www.w3.org/2000/svg"
                  width="125"
                  height="125"
                  viewBox="0 0 125 125"
                  fill="none"
                  $visible={anchor2visible}
                >
                  <g clipPath="url(#clip0_560_2891)">
                    <path
                      d="M46.041 75.2061C48.7053 78.7208 52.4095 81.4081 56.8704 82.7118C61.3313 84.0155 65.8995 83.7457 70.0373 82.2188"
                      stroke="#40E0EC"
                      strokeWidth="6"
                      strokeLinecap="round"
                    />
                    <path
                      d="M74.7137 66.2216C76.9225 66.8671 79.498 64.7045 80.4663 61.3913C81.4345 58.0781 80.4289 54.869 78.2201 54.2235C76.0113 53.578 73.4358 55.7406 72.4675 59.0538C71.4993 62.367 72.5049 65.5761 74.7137 66.2216Z"
                      fill="#40E0EC"
                    />
                    <path
                      d="M50.7176 59.2089C52.9264 59.8544 55.5019 57.6918 56.4702 54.3786C57.4384 51.0654 56.4328 47.8563 54.224 47.2108C52.0152 46.5653 49.4397 48.7279 48.4714 52.0411C47.5032 55.3543 48.5088 58.5634 50.7176 59.2089Z"
                      fill="#40E0EC"
                    />
                    <path
                      opacity="0.5"
                      d="M22.7223 51.027C28.232 32.1737 30.9869 22.7471 38.5555 18.6018C46.1241 14.4565 55.5507 17.2114 74.404 22.7211C93.257 28.2308 102.684 30.9857 106.829 38.5543C110.974 46.1229 108.22 55.5495 102.71 74.4028C97.2002 93.2559 94.4452 102.683 86.8766 106.828C79.3082 110.973 69.8812 108.218 51.0282 102.709C32.1749 97.199 22.7483 94.4441 18.603 86.8754C14.4576 79.307 17.2126 69.8801 22.7223 51.027Z"
                      stroke="#40E0EC"
                      strokeWidth="6"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_560_2891">
                      <rect
                        width="100"
                        height="100"
                        fill="white"
                        transform="translate(28.748 0.697266) rotate(16.2906)"
                      />
                    </clipPath>
                  </defs>
                </HelloItemIcon3p2>
              </HelloItemIcon>
              <HelloItemTitle>No design headaches</HelloItemTitle>
              <HelloItemText>
                Say goodbye to the stress of designing and coding templates from scratch.
              </HelloItemText>
            </HelloItemCover>
          </HelloItem>
        </HelloContentRight>
      </HelloContent>
    </Hello>
  )
}

export default HelloComponent
